import { createConsumer, type Consumer, type Subscription } from '@rails/actioncable'
import * as Sentry from '@sentry/vue'

let consumer: Consumer | null = null

// Lazy singleton — коннектится при первом subscribe.
// Reconnect handled by ActionCable internally.
export function getConsumer(): Consumer {
  consumer ??= createConsumer('/cable')
  return consumer
}

// Для логаута — закрываем соединение, дальше getConsumer() поднимет новое
// (и подсосёт новый jwt-cookie у нового пользователя).
export function disconnectConsumer(): void {
  consumer?.disconnect()
  consumer = null
}

export interface ResilientSubscriptionOptions<T> {
  // Параметры канала: строка имени ('UserChannel') или объект { channel, ...params }.
  params: string | ({ channel: string } & Record<string, unknown>)
  onReceived: (data: T) => void
  // Вызывается при ПОВТОРНОМ подключении (не на первом connect): события за время
  // разрыва потеряны, экран дёргает рефетч. Опционально — каналам без своего
  // состояния (например чисто push) ресинк не нужен. См. H-fe2 аудита.
  onResync?: () => void
}

// Подписка с восстановлением после разрыва (H-fe2) и логированием отказов (H-fe3).
//
// connected(): ActionCable зовёт его на каждом (ре)коннекте, включая первый.
// Первый connect ничего не ресинкает — данные уже загружены onMounted-фетчем
// экрана; onResync дёргается со второго connected() (после реального разрыва),
// чтобы подтянуть пропущенное за время оффлайна.
//
// rejected(): сессия потеряна между createConsumer и subscribe (истёк cookie) —
// подписка молча мертва, real-time не работает, а юзер думает что жив. Логируем.
// disconnected(): соединение упало (для отладки в Sentry, не алерт).
export function createResilientSubscription<T>(
  options: ResilientSubscriptionOptions<T>,
): Subscription {
  const channelName =
    typeof options.params === 'string' ? options.params : options.params.channel
  let connectedOnce = false

  return getConsumer().subscriptions.create(options.params, {
    received(data: T) {
      options.onReceived(data)
    },
    connected() {
      if (connectedOnce) {
        options.onResync?.()
      } else {
        connectedOnce = true
      }
    },
    rejected() {
      Sentry.captureMessage(`ActionCable subscription rejected: ${channelName}`, 'warning')
    },
    disconnected() {
      Sentry.addBreadcrumb({
        category: 'cable',
        level: 'info',
        message: `ActionCable subscription disconnected: ${channelName}`,
      })
    },
  })
}
